import { Component, ViewEncapsulation, Inject, OnDestroy, AfterViewInit } from '@angular/core';
import { Routes, Router, RouterModule, ActivatedRoute, NavigationCancel, NavigationError, NavigationStart, NavigationEnd, RoutesRecognized, NavigationExtras } from '@angular/router';
import { Idle, DEFAULT_INTERRUPTSOURCES, IdleExpiry, SimpleExpiry } from '@ng-idle/core';
import { TranslateService } from '../framework/i18n/translate.service';
import { EventBusService } from '../framework/eventbus.service';
import { LocalStorageService } from '../framework/localstorage.service';
import { PlatformLocation } from '@angular/common';
import { CanComponentDeactivate } from './login/can-deactivate-guard.service';
import { HostListener } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AuthService } from './login/auth.service';
import { SharedService } from './shared/services/shared.service';
import { Title } from '@angular/platform-browser';
import { AppConfig } from './app.config';

import { environment } from '../environments/environment';
import { HooksService } from './shared/services/hooks.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Event as RouterEvent } from "@angular/router";
import { DOCUMENT } from "@angular/common";
import { filter, takeUntil } from 'rxjs/operators';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { IdTokenClaims } from '@azure/msal-common';
import { AccountInfo, EventMessage, EventType, AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { ClarityService } from './clarity.service';
import { PreAuthService } from './pre-auth/pre-auth.service';
import { ChatService } from './shared/services/chat.service';
import { ApplicationInsightsService } from './shared/services/application-insights.service';

declare var window: any;
declare var jQuery: any;
declare var document: any;

function promptUser(event: VersionReadyEvent): boolean {
  return true;
}

type LinkPolicyType = 'signin' | 'password-reset' | 'restart-signin' | 'restart-password-reset';
type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string,
  tfp?: string,
};

@Component({
  selector: 'pa-app',
  templateUrl: './app.template.html',
  styleUrls: ['./app.style.css'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    HooksService,
    AuthService,
    EventBusService,
    SimpleExpiry,
    { provide: IdleExpiry, useExisting: SimpleExpiry },
    Idle
  ]
})

export class AppComponent implements CanComponentDeactivate, AfterViewInit {
  translatedText: string;
  isAuthyError: boolean = false;
  // Alert user about Browser reload & Browser close event in PA
  @HostListener('window:beforeunload')
  canDeactivate(): any {
    return !this._service.checkLoggedIn();
  }
  private readonly _destroying$ = new Subject<void>();
  stageName: any;
  currentUrl: String;
  splitedUrl: any;
  translateTitle: string;
  dynamicTitle: string;
  waitMessage: string;
  authErrorMsg: string;
  okButtonText: string;
  default_chat_exp: string;
  isAuthyLoggedIn = false;
  clientColor?: string;
  clientLogoUrl?: string;
  isEmbeddedMessagingInitialized = false;
  currentChatText = '';
  eventListenersSetup = false;
  isSwitchChatEnabled = false;
  preChatForm = true;
  // isAuthyGateOpen = false;
  clientLocale?: string;
  inviteKey?: string;
  authyPolicyType : string;
  emailAddress: string;

  constructor(
    private _hookService: HooksService,
    private _translate: TranslateService,
    private _eventBus: EventBusService,
    private idle: Idle,
    private _location: PlatformLocation,
    private _router: Router,
    private _route: ActivatedRoute,
    private _service: AuthService,
    private localStorage: LocalStorageService,
    private shareService: SharedService,
    private _preAuthService: PreAuthService,
    private titleService: Title,
    private swUpdate: SwUpdate,
    private _clarityService: ClarityService,
    private _appInsights: ApplicationInsightsService,
    @Inject(DOCUMENT) private document: any,
    @Inject(MsalService) private authService: MsalService,
    @Inject(MsalBroadcastService) private msalBroadcastService: MsalBroadcastService,
    private chatService: ChatService,
  ) {
    const env = environment;
    this.default_chat_exp = env.default_chat_exp;
    this.authErrorMsg = this._translate.instant('AUTH_ERROR_MSG');
    this.okButtonText = this._translate.instant('COMMONS_OKAY');

    this._eventBus.sessionInvalidReceivedAnnounced$.subscribe(item => {
      if (!!item && item['isSessionValid']) {
        this.authErrorMsg = this._translate.instant('INVALID_SESSION');
        this.okButtonText = this._translate.instant('COMMONS_OKAY');
      }
    })

    this._eventBus.languageInInviteReceived$.subscribe(item => {
      if (!!item && item['isSessionValid']) {
        this.authErrorMsg = this._translate.instant('INVALID_SESSION');
        this.okButtonText = this._translate.instant('COMMONS_OKAY');
      } else {
        this.authErrorMsg = this._translate.instant('AUTH_ERROR_MSG');
        this.okButtonText = this._translate.instant('COMMONS_OKAY');
      }

      if (!!item && item['translationsLanguage']) {
        if (this._translate.currentLang !== item['translationsLanguage']) {
          this._translate.use(item['translationsLanguage']);
        }
      }      
      if (!!item && item['receivedLanguage']) {
        if (this.chatService.isAIChatExperience()) {
          // Hide Salesforce chat in case it was loaded earlier (e.g., base URL fallback)
          this.chatService.hideSalesforceChat();
          const lang = item['receivedLanguage'];
          if (!this.chatService.isAIChatbotInitialized) {
            this.chatService.initI18n(navigator.language || lang).then(() => {
              this.isSwitchChatEnabled = true;
              this.chatService.initAIChatbot();
            });
          } else {
            // Language changed while chatbot is already running (e.g., language-selection page)
            this.chatService.changeLanguage(lang);
          }
        } else {
          // Hide AI chatbot in case it was loaded earlier (e.g., base URL fallback)
          this.isSwitchChatEnabled = false;
          this.chatService.showSalesforceChat();
          this.loadEmbeddedMessaging(AppConfig.getSalesForceChatLanguage(item['receivedLanguage']));
          // Update chat button text when language changes
          this.updateEmbeddedMessagingButtonText();
        }
      }
    });
    
    _router.events.subscribe(
      (event: RouterEvent): void => {
        var globalModal = document.getElementById("globalModal");

        if (event instanceof NavigationStart) {
          this.waitMessage = this._translate.instant('WAIT_ON_AGGREMENT_MODAL_TEXT');
          globalModal.style.display = "block";
          
          // Call clarity.identify whenever new route id recognized
          if (this._clarityService.isInitialized) {
            // select [2] from route id if available else [1]
            const routeId = event.url.split('/')[2] || event.url.split('/')[1];
            let inviteKey = this.localStorage.getItem("key");
            let inviteId = this.localStorage.getItem("inviteId");
              
            if (routeId !== 'login' && routeId !== 'set-password' && !!routeId && !!inviteKey && !!inviteId) {
              if (typeof window.clarity === 'function') {
                window.clarity('identify', inviteKey, inviteId, routeId, inviteKey);
              }
              // inviteKey is an opaque internal id, not PII - safe to use as the App Insights user id
              this._appInsights.setAuthenticatedUser(inviteKey, this.localStorage.getItem('account_id'));
              this._appInsights.setInviteKey(inviteKey);
            }
          }
        } else if (event instanceof NavigationEnd || event instanceof NavigationError) {
          globalModal.style.display = "none";
        } else if(event instanceof NavigationCancel) {
          this.logoutApp();
          globalModal.style.display = "none";
        } else if (event instanceof RoutesRecognized) {
          if (event['id'] !== 1 && event['url'] === '/' && event['urlAfterRedirects'] === '/') {
            this.logoutApp();
          }
        }
      }
    );

    _location.onHashChange(() => {
      // console.log(_router.url, 'LOGIN DEBUG');
      if (_router.url === '/')
        window.history.forward();

      if (_router.url.indexOf('login') > 0)
        window.history.forward();

      // Check user_email from localDB to confirm the logged in context
      if (_service.checkLoggedIn()) {
        let locHref = window.location.href.split('#/');

        if (!!locHref && locHref[1] !== "" /*&& locHref[1].indexOf("yoti-") === -1*/) {
          jQuery('#logout_confirmation_modal').modal('show');
          window.history.forward();
        } else {
          // Do not show logoutmodal when refresh/edit url, as page already redirected to login and propagtion could not be stopped.
          // Handled clearing of localstorage in NavigationCancel and RoutesRecognized above
        }
      } else {
        jQuery('#back-modal').modal('hide');
      }
    });

    let _timeout: any = this.localStorage.getItem('session_timeout');

    if (_timeout != null) {
      _timeout = parseInt(_timeout);
    } else {
      // one hour
      _timeout = 3600;
    }

    idle.setIdle(_timeout);
    idle.setTimeout(_timeout);

    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // Subscribe to idle events. Add your logic on how the application should respond, such as displaying
    // a warning dialog onIdleStart, or redirecting to logout page onTImeout, etc.
    idle.onIdleStart.subscribe(() => {
    });

    idle.onIdleEnd.subscribe(() => {
    });

    idle.onTimeoutWarning.subscribe((countdown: number) => {
    });

    idle.onTimeout.subscribe(() => {
      this.doLogout();
    });

    // start watching for idleness right away.
    idle.watch();
  }

  doLogout() {
    this._eventBus.logoutAnnounced({
      dummy: 'logout'
    });

    this.closeModal();
  }
  
  ngOnInit() {
    let isAuthyCallback = this.localStorage.getItem("isAuthyCallback");
    if (!!isAuthyCallback) {
      this.doAuthyCheck();
      this._eventBus.authyLogOutAnnounced$.subscribe(item => {
        this.authService.logout();
      });
    }
    
    if (this.swUpdate.isEnabled && environment.production) {
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(evt => {
          if (promptUser(evt)) {
            // Reload the page to update to the latest version
            document.location.reload();
          }
        });
    }
    
    this.shareService.customFormVariables = {};
    this.pageRefreshAndroid();
    this.setDefaultLang();
    this.setDynamicTitle();

    this.localStorage.setItem('websdk_vendor',environment.websdk_vendor);
    this.localStorage.setItem('verification_api_vendor',environment.verification_api_vendor);
    this.localStorage.setItem("invite_url", window.location.href);
    // console.log('$$$WEB - VENDOR$$$ ' + this.localStorage.getItem('websdk_vendor'));
    // console.log('$$$API - VENDOR$$$ ' + this.localStorage.getItem('verification_api_vendor'));

    // Base URL detection: keep chatbot context intact and let userInfo determine pre-chat behavior
    if (window.location.href.indexOf('key') < 0) {
      try {
        if (!this.shareService.isSupportedMobileOs() ) {
          this.shareService.logFlow("app","unsupported-device", this.shareService.getDeviceDetails());
          this._router.navigate(['/unsupported-device'], {queryParams:{unsupportedDeviceVersion: true}});
          return;
        }

        if (!this.shareService.isSupportedBrowserVersion() ) {
          this.shareService.logFlow("app","unsupported-browser", this.shareService.getDeviceDetails());
          this._router.navigate(['unsupported-browsing']);
          return;
        }
      } catch(error) {
      }
    }
  }
  
  ngAfterViewInit(): void {
    // If no invite key in URL (base URL like localhost:4200 or pa-intl-qa.fadv.net),
    // languageInInviteReceived$ will never fire. Load the default chat experience.
    if (window.location.href.indexOf('key') < 0) {
      if (this.chatService.isAIChatExperience()) {
        const lang = navigator.language || this.localStorage.getItem('language') || 'en';
        this.chatService.initI18n(lang).then(() => {
          this.isSwitchChatEnabled = true;
          this.chatService.initAIChatbot();
        });
      } else {
        this.loadEmbeddedMessaging('en');
      }
    }
  }

  setDefaultLang() {
    // console.log(' #--> Now the browser default lang is ' + navigator.language.toLowerCase());
    if (environment &&
      environment.hasOwnProperty('languageEditEnabled') &&
      environment.languageEditEnabled === true) {
        // console.log('languageEditEnabled hense setting default lang is : zu' );
        this.shareService.setDefaultLang('zu');
      } else {
        this.shareService.setDefaultLang(navigator.language.toLowerCase());
      }
  }

  pageRefreshAndroid() {
    window.addEventListener('load', function () {
      let maybePreventPullToRefresh = false;
      let lastTouchY = 0;
      let touchstartHandler = function (e) {
        if (e.touches.length != 1) return;
        lastTouchY = e.touches[0].clientY;
        // Pull-to-refresh will only trigger if the scroll begins when the
        // document's Y offset is zero.
        maybePreventPullToRefresh = window.pageYOffset === 0;
      };

      let touchmoveHandler = function (e) {
        let touchY = e.touches[0].clientY;
        let touchYDelta = touchY - lastTouchY;
        lastTouchY = touchY;

        if (maybePreventPullToRefresh) {
          // To suppress pull-to-refresh it is sufficient to preventDefault the
          // first overscrolling touchmove.
          maybePreventPullToRefresh = false;
          if (touchYDelta > 0) {
            e.preventDefault();
            return;
          }
        }
      };

      document.addEventListener('touchstart', touchstartHandler, false);
      document.addEventListener('touchmove', touchmoveHandler, false);
    });
  }

  keyboardEventMobile() {
    jQuery('body').on('focusin', 'input, textarea', function (event) {
      if (navigator.userAgent.indexOf('Android') > -1) {
        let scroll = jQuery(this).offset();
        window.scrollTo(0, scroll);
      }
    });
  }

  closeModal() {
    jQuery('.modal-backdrop').css({
      'position': 'relative'
    });
  }

  closeErrorModal() {
    jQuery('#error-modal').modal('hide');
  }

  closeAuthErrorModal() {
    window.track_event("close auth error modal", { stage_name: 'app', action_value: 'click' }, true, true);
    this.logoutApp();
    this._router.navigate(['./login']);
    jQuery('#auth-error-modal').modal('hide');
  }

  setDynamicTitle() {
    this._router.events.subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.stageName = e.url;
        this.dynamicTitleDisplay(this.stageName);
      }
    });
  }

  dynamicTitleDisplay(NavEnd: String) {
    this.splitedUrl = NavEnd.split("/");
    this.stageName = (this.splitedUrl[this.splitedUrl.length - 1]);
    if (this.stageName == ':token') {
      this.stageName = (this.splitedUrl[this.splitedUrl.length - 2])
    }

    if (this.translateTitle !== null && this.translateTitle !== undefined && this.translateTitle !== '') {
      this.dynamicTitle = this._translate.instant(this.translateTitle);
      this.dynamicTitle = this.dynamicTitle.charAt(0).toUpperCase() + this.dynamicTitle.slice(1).toLowerCase();
      for (let i = 0; i < this.dynamicTitle.length; i++) {
        if (this.dynamicTitle.charAt(i) == ' ')
          this.dynamicTitle.charAt(i + 1).toUpperCase()
      }
      this.dynamicTitle = 'First Advantage - ' + this.dynamicTitle;
      this.titleService.setTitle(this.dynamicTitle);
    } else {
      this.titleService.setTitle('First Advantage');
    }
  }

  logoutApp() {
    let isAuthyCallback = this.localStorage.getItem("isAuthyCallback");
    if (isAuthyCallback) {
      this.authService.logout();
    }
    this._eventBus.hideHeaderAnnounced({ userInfoShownValue: false });
    window.track_event("logout and delete session", { stage_name: 'app', action_value: 'continue click' }, true, true);
    this.shareService.raisingEventTermsServiceHide(this._eventBus, true);
    this._service.logout();
 
    if (window.hasOwnProperty('safeNotifyHost')) window.safeNotifyHost('signed_out', null);
  }

  @HostListener('window:beforeunload')
  async ngOnDestroy() {
    const locHref = window.location.href;
    if (!!locHref && locHref.indexOf("yoti-") === -1) {
      this.logoutApp();
    }

    // TODO: Shutdown not needed — chatbot persists across navigation
    // if (this.chatService.isAIChatbotInitialized) {
    //   this.chatService.shutdownAIChatbot();
    // }
    
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  loadEmbeddedMessaging(languageCode) {
    let that = this;
    
    // Prevent multiple initializations
    if (this.isEmbeddedMessagingInitialized) {
      // Just update the button text for language changes
      this.updateEmbeddedMessagingButtonText();
      return;
    }
    
    // Check if the embedded messaging is already loaded
    if (!window.embeddedservice_bootstrap) {
      var s = document.createElement('script');
      s.setAttribute('src', environment.miawChatEmbeddedServiceMinJS);
      s.onload = function() {
        that.initEmbeddedMessaging(languageCode);
      };
      s.onerror = function() {
        console.error('Failed to load Embedded Messaging bootstrap script');
      };
      var scriptId = 'embeddedMessagingScript';
      s.id = scriptId;
      document.body.appendChild(s);
    } else if (!this.isEmbeddedMessagingInitialized) {
      this.initEmbeddedMessaging(languageCode);
    }
  }

  initEmbeddedMessaging(languageCode) {
    try {
      let lang = !!languageCode ? languageCode : 'en';
      
      if (window.embeddedservice_bootstrap) {
        // Set language based on the provided language code
        window.embeddedservice_bootstrap.settings.language = lang;
        
        // Initialize the embedded messaging with test environment configuration
        window.embeddedservice_bootstrap.init(
          environment.miawOrgId,
          environment.miawEswConfigDevName,
          environment.miawCommunityEndpointURL,
          {
            scrt2URL: environment.miawScrt2URL
          }
        );
        
        // Mark as initialized and update button text
        this.isEmbeddedMessagingInitialized = true;
        this.updateEmbeddedMessagingButtonText();
      }
    } catch (err) {
      console.error('Error loading Embedded Messaging: ', err);
    }
  }

  updateEmbeddedMessagingButtonText() {
    // Only update if embedded messaging is initialized
    if (!this.isEmbeddedMessagingInitialized) {
      return;
    }
    
    // Get the translated text for chat button
    const chatText = this._translate.instant('CHAT_ONLINE');
    this.currentChatText = chatText; // Store current text
    
    // Wait for button to be available in DOM and update the text
    setTimeout(() => {
      this.addDynamicChatButtonStyle(chatText);
      this.setupEmbeddedMessagingEventListeners(); // Set up event listeners to monitor changes
    }, 1000);
  }

  addDynamicChatButtonStyle(chatText: string) {
    // Use CSS custom property to update the pseudo-element content
    setTimeout(() => {
      const chatButton = document.querySelector('.embeddedMessagingConversationButton') as HTMLElement;
      if (chatButton) {
        // Set the CSS custom property --pseudo-text with the translated text
        chatButton.style.setProperty('--pseudo-text', `'${chatText}'`);
      }
    }, 500);
  }

  setupEmbeddedMessagingEventListeners() {
    // Only set up event listeners once to prevent duplicates
    if (this.eventListenersSetup) {
      return;
    }
    
    // Set up event listeners for Salesforce Embedded Messaging events using window.addEventListener
    
    // Listen for when the embedded messaging is ready
    window.addEventListener('onEmbeddedMessagingReady', () => {
      setTimeout(() => {
        this.reapplyChatButtonText();
      }, 500);
    });
    
    // Listen for when the chat window is closed (button becomes visible again)
    window.addEventListener('onEmbeddedMessagingWindowClosed', () => {
      // Apply immediately and also after short delay for safety
      this.reapplyChatButtonText();
      setTimeout(() => {
        this.reapplyChatButtonText();
      }, 100);
    });
    
    // Mark event listeners as set up
    this.eventListenersSetup = true;
  }
  
  reapplyChatButtonText() {
    const chatButton = document.querySelector('.embeddedMessagingConversationButton') as HTMLElement;
    if (chatButton) {
      // Always get the current translation instead of using stored value
      const currentTranslation = this._translate.instant('CHAT_ONLINE');
      this.currentChatText = currentTranslation; // Update stored value
      const expectedText = `'${currentTranslation}'`;
      
      // Always reapply the current translation immediately
      console.log('Reapplying chat button text:', expectedText);
      chatButton.style.setProperty('--pseudo-text', expectedText);
    }
  }

  doAuthyCheck() {
    const hash = window.location.hash;
    const search = new URLSearchParams(decodeURIComponent(hash.substring(1)));
    const stateParts = search.get('state')?.split('|') ?? [];
    const clientInfoEncoded = stateParts?.find((p) => p.startsWith('client_info='))?.split('=')[1];
    const clientInfoDecoded = clientInfoEncoded ? window.atob(clientInfoEncoded) : null;
    const clientInfo = clientInfoDecoded ? JSON.parse(clientInfoDecoded) : null;
    this.isAuthyError = false;

    if (clientInfo) {
      this.clientColor = clientInfo.color;
      this.clientLogoUrl = clientInfo.logo;
      this.inviteKey = clientInfo.invite;
      this.authyPolicyType = clientInfo.authyPolicyType;
      this.emailAddress = clientInfo.emailAddress;
      this.clientLocale = clientInfo.locale;
    }
    
    // initializes MSAL and configures redirect route.
    this.authService.handleRedirectObservable().subscribe();
    const query = new URLSearchParams(window.location.search);

    // interaction none means that no auth is in progress.
    // and the application is ready to start.
    let that = this;
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(async () => {
        that.setIsAuthyLoggedIn();
        const allAccounts = that.authService.instance.getAllAccounts();

        if (allAccounts.length > 0) {
          let len = allAccounts.length;
          if (len == 1) {
            that.setActiveAccount(allAccounts[0]);
          } else {
            let account = allAccounts.find(act => act?.idTokenClaims?.invite === that.inviteKey);
            if (account) {
              that.setActiveAccount(account);
            } else {
              console.error('Authy account not found matching invite key******', that.inviteKey);
            }
          }
        }
        // if the magic link is used to login, redirect to the appropriate policy
        if (query.has('type')) {
          const type = query.get('type') as LinkPolicyType;

          // email will always be available for the restart links
          let emailAddress = query.get('email')?.replace(' ', '+');
          // invite key will be available if it's a use case where the invite was provided
          let inviteKey = query.get('invite');
          // if it's not a restart link there will be a token, the email, and potentially the invite, will be included.
          const token = query.get('token');
          if (token) {
            // It's up to the application if it wants to do this or not, but the token could be decoded here
            // and the expiration time could be checked to see if it's still valid. If it's expired you could redirect
            // the user directly to the appropriate policy to send a new email. However, if the users system clock or timezone
            // is incorrect it could cause this check to fail. This has caused some support issues, so it may be best to just let authy do the
            // actual verifications. However, it doesn't mean the token can't be decoded to get information. There isn't a lot of information for
            // link tokens, but, in addition to the default claims, it will contain the authy token id, the sub, and the invite key(if provided).
            //const decoded = jwtDecode<{ sub: string; invite?: string; }>(token);
            const decoded = jwtDecode<{ sub: string; invite?: string; exp?: number }>(token);
            // the sub will always be in the token, and for link tokens it will always be the email address.

            emailAddress = decoded.sub;
            // the invite key may, or may not exist in the token, depending on the context.
            inviteKey = decoded.invite?.trim().length ? decoded.invite : null;
            console.log('Decoded Link token', decoded);
            that.localStorage.setItem("inviteKey", inviteKey);

            const now = Date.now() / 1000;

            if (decoded.exp && decoded.exp <= now) {
              that.shareService.logAuthyFlow("app", "authy-token-expired");
              that.localStorage.setItem("authyPolicyType", type);
              that.localStorage.setItem("isAuthyTokenExpired", true);
              that.localStorage.setItem("emailAddress", emailAddress);

              return;
            }
          }
          // if the user is logged in perform a logout and redirect back to the current page.
          if (that.isAuthyLoggedIn) {
            let locHref = window.location.href;
            if (!!inviteKey) {
              that.shareService.logAuthyFlow("app", "authy-logout-redirect", "for type " + type);
              await that._preAuthService.fetchAuthEngineDetails(inviteKey).then(function (resp) {
                let authyPrefValue = resp['authy_pref_value'];
                if (authyPrefValue === "4") {
                  authyPrefValue = "1";
                }
                let mfaType = that.shareService.getMfaType(authyPrefValue);
                
                const authority = environment.invitePolicies['signin'][mfaType];
                that.authService.logoutRedirect({
                  authority: authority,
                  postLogoutRedirectUri: locHref,
                });
              });
            } else {
              that.authService.logoutRedirect({
                authority: environment.policies.signinMagicLink.authority,
                postLogoutRedirectUri: window.location.href,
              });
            }
            return;
          }

          // this lookup is by email address, but it could be by invite key
          let authority = environment.magicLinkPolicies[type];
          console.log("type and authority --->", type, authority);

          let state: string | undefined = undefined;

          if (that.clientColor || that.clientLogoUrl || that.inviteKey) {
            state = `client_info=${window.btoa(
              JSON.stringify({
                color: that.clientColor,
                logo: that.clientLogoUrl,
                invite: that.inviteKey,
                locale: that.clientLocale
              })
            )}`;
          }
          that.shareService.logAuthyFlow("app","authy-get-mfaType", "inviteKey " + inviteKey);
          if (!!inviteKey) {
            await that._preAuthService.fetchAuthEngineDetails(inviteKey).then(function (response) {
              let authyPrefValue = response['authy_pref_value'];
              let mfaType = that.shareService.getMfaType(authyPrefValue);
              if ((mfaType === 'magiclink' || mfaType === 'sms') && (type === 'password-reset' || type === 'restart-password-reset')) {
                authority = environment.magicLinkPolicies[type + '-sms'];
              }

              if (authyPrefValue === "4") {
                authyPrefValue = "1";
                mfaType = that.shareService.getMfaType(authyPrefValue);
              }
              //If required get the action if signup/signin for determining the mfaType.
              let clientColor = response['client_color'];
              let clientLogoUrl = response['client_logo_url'];
              let clientLocale = response['authy_locale'] ;
              that.localStorage.setItem("authy_locale", response['authy_locale']);

              that.localStorage.setItem('mfaType', mfaType);
              if (type === 'restart-signin') {
                // restartSigninPolicies is just a mapping of policies by mfa type,
                // this just makes it easier than conditioning on each type
                authority = environment.restartSigninPolicies[mfaType];
              }
              that.shareService.logAuthyFlow("app","authy-login-redirect", "for type " + type);
              let state = `client_info=${window.btoa(
                JSON.stringify({
                  color: clientColor,
                  logo: clientLogoUrl,
                  invite: inviteKey,
                  authyPolicyType: type,
                  emailAddress: emailAddress,
                  locale: clientLocale
                })
              )}`;
              that.authService.loginRedirect({
                authority: authority,
                scopes: environment.scopes,
                prompt: 'login',
                // if there isn't a token, provide the email as the login hint
                loginHint: !token ? emailAddress ?? '' : undefined,
                extraQueryParameters: {
                  invite: inviteKey,
                  // if there is a token, use it as the id token hint
                  id_token_hint: token ?? '',
                  clientColor: encodeURIComponent(clientColor),
                  clientLogoUrl: encodeURIComponent(clientLogoUrl),
                  ui_locales: encodeURIComponent(clientLocale)
                },
                state
              });
            });
          }
        } else {
          if (that.isAuthyLoggedIn && !that.isAuthyError && that._router.url === '/') {
            that.localStorage.setItem("isAuthyLoggedIn", true);
            that.shareService.logAuthyFlow("app", "authy-redirect-to-preauth", that.shareService.getDeviceDetails());

            // this needs to make sure it's on the default route, otherwise the user will be redirected
            // to the home page when they are on a different route and reloads the page.
            that._router.navigate(['/invite/authytoken']);
          } else {
            // In certain conditions you can get a flash of the signed-out page
            // the timeout is 3secs just to make sure the loader shows given the built in
            // delay of the loader component.
            if (!that.isAuthyError) {
              that.showGlobalModal();
              that.shareService.logAuthyFlow("app", "authy-login-redirect", "from Inprogress");
              setTimeout(async () => {
                let authySub = window.sessionStorage.getItem("Authy_subject");
                if (!!that.inviteKey) {
                  await that.doLoginRedirect(that.inviteKey, authySub);
                } else {
                  that.authService.loginRedirect({
                    authority: environment.policies.signinMagicLink.authority,
                    scopes: [],
                  });
                }
              }, 1000);
            }
          }
        }
      });

      this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        // const idToken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;
        let accessToken = payload.accessToken;
        that.isAuthyError=false;
        that.localStorage.setItem('access_token', accessToken);
        window.track_event("accesstoken stored", { stage_name: 'app', action_value: 'clicked' }, true, true, true);
        that.shareService.logAuthyFlow("app", "authy-login-success");
        
        // AC3/AC4 Authy chatbot_session_key sync disabled.
        // console.log('🔐 Authy LOGIN_SUCCESS: extracting chatbot_session_key from token');
        // that.syncChatbotSessionKeyFromAuthyToken(idToken, payload.idToken);
        // Mark that Authy token has been processed - chatbot can now initialize
        // that.isAuthyGateOpen = true;
        // console.log('✅ Authy gate opened: chatbot initialization allowed');
        
        return result;
      });
      
      this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(async (result: EventMessage) => {
        console.log('MSAL Error', result.eventType);
        // when the magic link is used and the token is invalid
        const { error } = result;
        that.shareService.logAuthyFlow("app","authy-error", "error message " + (error?.message) + " , " + that.shareService.getDeviceDetails());
        
        let state: string | undefined = undefined;
        
        if (that.clientColor || that.clientLogoUrl || that.inviteKey) {
          state = `client_info=${window.btoa(
            JSON.stringify({
              color: that.clientColor,
              logo: that.clientLogoUrl,
              invite: that.inviteKey,
              locale: that.clientLocale
            })
          )}`;
        }

        if (error?.message.includes('AADB2C90240')) {
          //alert the user //TODO
          return;
        }
        
        // when the magic link is used and the token is expired
        if (error?.message.includes('AADB2C90208')) {
          that.isAuthyError = true;
          // Set language before navigating to expired-link
          const authyLocale = that.clientLocale || that.localStorage.getItem("authy_locale");
          if (authyLocale) {
            (async () => {
              await that.shareService.setLanguageFromClientInfo(authyLocale, that._eventBus);
              that._router.navigate(['expired-link']);
            })();
          } else {
            that._router.navigate(['expired-link']);
          }
          
          return;
        }

        // when there was a failure in sending the email
        if (error?.message.includes('AADB2C90261')) {
          //alert the user //TODO
          return;
        }
        // Checking for the forgot password error. Learn more about B2C error codes at
        // https://learn.microsoft.com/azure/active-directory-b2c/error-codes
        const loginHint =
          error.message
            .split('\n')
            .find((p) => p.includes('login_hint'))
            ?.split(':')?.[1]
            ?.trim()
            .replace(' ', '+') ?? '';
        // when the password reset is invoked from another policy.
        if (error?.message.includes('AADB2C90118')) {
          that.isAuthyError = true;
          that.showGlobalModal();
          if (!!that.inviteKey) {
            await this._preAuthService.fetchAuthEngineDetails(that.inviteKey).then(function (response) {
              let mfaType = that.shareService.getMfaType(response['authy_pref_value']);
              let locale = response['authy_locale'];
              that.localStorage.setItem("authy_locale", response['authy_locale']);
              let authority = undefined;
              if (mfaType === 'magiclink' || mfaType === 'sms') {
                authority = environment.policies.passwordResetSms.authority;
              } else {
                authority = environment.policies.passwordReset.authority;
              }
              that.authService.loginRedirect({
                authority: authority,
                scopes: environment.scopes,
                state,
                extraQueryParameters: {
                  invite: that.inviteKey,
                  clientColor: encodeURIComponent(that.clientColor ?? ''),
                  clientLogoUrl: encodeURIComponent(that.clientLogoUrl ?? ''),
                  ui_locales: encodeURIComponent(locale ?? ''),
                },
                loginHint
              });
            });
          } else {
            that.authService.loginRedirect({
              authority: environment.policies.passwordReset.authority,
              scopes: environment.scopes,
              state,
              extraQueryParameters: {
                invite: that.inviteKey,
                clientColor: encodeURIComponent(that.clientColor ?? ''),
                clientLogoUrl: encodeURIComponent(that.clientLogoUrl ?? ''),
                ui_locales: encodeURIComponent(this.localStorage.getItem("authy_locale") ?? ''),

              },
              loginHint
            });
          }
          return;
        }

        // it's not possible to add hyperlinks to a policy page, so a custom error is needed
        // this is the custom error for when the start again link is clicked after the email verification magic link is clicked
        // when cancel link is clicked
        if (error?.message.includes('AAD_Custom_Authy0001') || error?.message.includes('AADB2C90091')) {
          that.isAuthyError = true;
          that.showGlobalModal();
          if (!!that.inviteKey) {
            await that.doLoginRedirect(that.inviteKey, loginHint);
          } else {
            that.authService.loginRedirect({
              authority: environment.policies.signinMagicLink.authority,
              scopes: environment.scopes,
              state,
              extraQueryParameters: {
                invite: that.inviteKey,
                clientColor: encodeURIComponent(that.clientColor ?? ''),
                clientLogoUrl: encodeURIComponent(that.clientLogoUrl ?? ''),
                ui_locales: encodeURIComponent(this.localStorage.getItem("authy_locale") ?? ''),

              },
              loginHint
            });
            return;
          }
        }

        if (error?.message.includes('AAD_Custom_Authy0005')) {
          that.isAuthyError = true;
          that.showGlobalModal();
          if (!!that.inviteKey) {
            await that.doLoginRedirect(that.inviteKey, loginHint, 'update-phonenumber');
          } else {
            that.authService.loginRedirect({
              authority: environment.policies.changePhoneNumber.authority,
              scopes: environment.scopes,
              state,
              extraQueryParameters: {
                invite: that.inviteKey,
                clientColor: encodeURIComponent(that.clientColor ?? ''),
                clientLogoUrl: encodeURIComponent(that.clientLogoUrl ?? ''),
                ui_locales: encodeURIComponent(this.localStorage.getItem("authy_locale") ?? ''),
              },
              loginHint
            });
            return;
          }
        }        
        
        // this is the custom error for when the start again link is clicked after the reset password magic link is clicked
        if (error?.message.includes('AAD_Custom_Authy0002')) {
          that.isAuthyError = true;
          that.showGlobalModal();
          if (!!that.inviteKey) {
            await this._preAuthService.fetchAuthEngineDetails(that.inviteKey).then(function (response) {
              let mfaType = that.shareService.getMfaType(response['authy_pref_value']);
              let locale = response['authy_locale'];
              that.localStorage.setItem("authy_locale", response['authy_locale']);
              let authority = undefined;
              if (mfaType === 'magiclink' || mfaType === 'sms') {
                authority = environment.policies.passwordResetSms.authority;
              } else {
                authority = environment.policies.passwordReset.authority;
              }
              that.authService.loginRedirect({
                authority: authority,
                scopes: environment.scopes,
                state,
                extraQueryParameters: {
                  invite: that.inviteKey,
                  clientColor: encodeURIComponent(that.clientColor ?? ''),
                  clientLogoUrl: encodeURIComponent(that.clientLogoUrl ?? ''),
                  ui_locales: encodeURIComponent(locale ?? '')
                },
                loginHint
              });
            });
          } else {
            that.authService.loginRedirect({
              authority: environment.policies.passwordReset.authority,
              scopes: environment.scopes,
              state,
              extraQueryParameters: {
                invite: that.inviteKey,
                clientColor: encodeURIComponent(that.clientColor ?? ''),
                clientLogoUrl: encodeURIComponent(that.clientLogoUrl ?? ''),
                ui_locales: encodeURIComponent(this.localStorage.getItem("authy_locale") ?? ''),
              },
              loginHint
            });
          }
          return;
        }
        
        if (error?.message.includes('AAD_Custom_Authy0003')) {
          that.isAuthyError = true;
          that.localStorage.setItem("inviteKey", that.inviteKey);
          that.localStorage.setItem("authyPolicyType", that.authyPolicyType);
          that.localStorage.setItem("isAuthyTokenExpired", true);
          that.localStorage.setItem("emailAddress", that.emailAddress);

          // Set language before navigating to expired-link
          const authyLocale = that.clientLocale || that.localStorage.getItem("authy_locale");
          if (authyLocale) {
            (async () => {
              await that.shareService.setLanguageFromClientInfo(authyLocale, that._eventBus);
              this._router.navigate(['expired-link']);
            })();
          } else {
            this._router.navigate(['expired-link']);
          }
          return;
        }
        
        if (error?.message.includes('AADB2C90075') && error.message.includes('REST-VerifyToken')) {
          //alert the user //TODO
          
          return;
        }

        // if there is an error this should redirect to a generic error page, unless it's something that can be recovered from,
        // such as the reset password requested error.
      });
  }

  setIsAuthyLoggedIn(): void {
    this.isAuthyLoggedIn = this.authService.instance.getAllAccounts().length > 0;
  }

  setActiveAccount(accountInfo: AccountInfo): void {
    if (!this.isAuthyLoggedIn) return;

    this.authService.instance.setActiveAccount(accountInfo);

    const claims = this.authService.instance.getActiveAccount()?.idTokenClaims as {
      name?: string;
      invite?: string;
      type? : string;
    };
    
    if (!claims) return;
    
    const inviteAuthType = claims.type;
    const inviteKey = claims.invite;
    this.shareService.logAuthyFlow("app", "authy-set-active-account", " inviteAuthType and inviteKey " + inviteAuthType + " and " + inviteKey);
    this.localStorage.setItem("inviteAuthType", inviteAuthType);
    this.localStorage.setItem("extension_invite", inviteKey);
    
    if (!inviteKey) return;
  }
  
  showGlobalModal() {
    let globalModal = document.getElementById("globalModal");
    globalModal.style.display = "block";
  }

  // private syncChatbotSessionKeyFromAuthyToken(idTokenClaims?: IdTokenClaimsWithPolicyId, rawIdToken?: string): void {
  //   let chatbotSessionKey = (idTokenClaims as any)?.chatbot_session_key;

  //   if (!chatbotSessionKey && rawIdToken) {
  //     try {
  //       const decodedToken = jwtDecode<any>(rawIdToken);
  //       chatbotSessionKey = decodedToken?.chatbot_session_key;
  //     } catch (error) {
  //       console.warn('Unable to decode Authy id token for chatbot_session_key extraction:', error);
  //     }
  //   }

  //   if (chatbotSessionKey) {
  //     console.log('📌 Found chatbot_session_key in Authy token, storing in browser localStorage');
  //     if (window.localStorage) {
  //       window.localStorage.setItem('chatbot_session_key', chatbotSessionKey);
  //     }
  //     return;
  //   }

  //   const existingKey = window.localStorage?.getItem('chatbot_session_key');
  //   if (existingKey) {
  //     console.log('🔄 Reusing existing chatbot_session_key from browser localStorage');
  //   } else {
  //     console.log('⚠️ No chatbot_session_key found in Authy token or browser localStorage');
  //   }
  // }

  async doLoginRedirect(inviteKey: string, loginHint : any, type?: string) {
    let that = this;
    await this._preAuthService.fetchAuthEngineDetails(inviteKey).then(function (response) {
      let authyPrefValue = response['authy_pref_value'];
      if (authyPrefValue === "4") {
        authyPrefValue = "1";
      }
      
      let mfaType = that.shareService.getMfaType(authyPrefValue);
      let clientColor = response['client_color'];
      let clientLogoUrl = response['client_logo_url'];
      let clientLocale = response['authy_locale'] ;
      that.localStorage.setItem("authy_locale", response['authy_locale']);      
        
        let state = `client_info=${window.btoa(
        JSON.stringify({
          color: clientColor,
          logo: clientLogoUrl,
          invite: inviteKey,
          locale: clientLocale
        })
      )}`;
      that.localStorage.setItem('mfaType', mfaType);
      let authority = environment.invitePolicies['signin'][mfaType];
      if (type === 'update-phonenumber') {
        authority = environment.magicLinkPolicies[type];
      }

      that.authService.loginRedirect({
        authority: authority,
        loginHint: loginHint ?? '',
        scopes: [],
        extraQueryParameters: {
          invite: inviteKey,
          clientColor: encodeURIComponent(clientColor),
          clientLogoUrl: encodeURIComponent(clientLogoUrl),
          ui_locales: encodeURIComponent(clientLocale),
        },
        state
      });
    });

  }
}
